import React, { useState, useEffect, useCallback, useRef } from "react";
import API_BASE from "./apiConfig";
import { useTheme, getT, getBrandColors } from "./ThemeContext";
import PageHeaderIcon from "./components/PageHeaderIcon";
import {
  FaCheckCircle, FaCalendarAlt, FaTimesCircle, FaBan,
  FaChevronDown, FaChevronUp, FaUserGraduate, FaUndo,
} from "react-icons/fa";
import { useDateTimeFormat } from "./utils/formatters";

// ─── Date helpers ─────────────────────────────────────────────────────────────
function isoDate(d) {
  return d.getFullYear() + "-" +
    String(d.getMonth() + 1).padStart(2, "0") + "-" +
    String(d.getDate()).padStart(2, "0");
}
function weekRange() {
  const now  = new Date();
  const day  = now.getDay();                       // 0=Sun
  const mon  = new Date(now); mon.setDate(now.getDate() - ((day + 6) % 7));
  const sun  = new Date(mon); sun.setDate(mon.getDate() + 6);
  return { start: isoDate(mon), end: isoDate(sun) };
}
function monthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end   = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { start: isoDate(start), end: isoDate(end) };
}
const TODAY = isoDate(new Date());
const WK    = weekRange();
const MO    = monthRange();

// ─── API ──────────────────────────────────────────────────────────────────────
const api = {
  get:  (path)       => fetch(`${API_BASE}${path}`).then(r => r.json()),
  put:  (path, body) => fetch(`${API_BASE}${path}`, {
    method:"PUT", headers:{"Content-Type":"application/json"},
    body: body ? JSON.stringify(body) : undefined,
  }).then(r => r.json()),
  post: (path, body) => fetch(`${API_BASE}${path}`, {
    method:"POST", headers:{"Content-Type":"application/json"},
    body: body ? JSON.stringify(body) : undefined,
  }).then(r => r.json()),
};

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS = {
  booked:           { bg:"#dbeafe", text:"#1d4ed8", label:"Booked" },
  attended:         { bg:"#dcfce7", text:"#166534", label:"Attended" },
  no_show:          { bg:"#fee2e2", text:"#991b1b", label:"No Show" },
  converted:        { bg:"#d1fae5", text:"#065f46", label:"Converted ✓" },
  never_signup:     { bg:"#f3f4f6", text:"#6b7280", label:"Declined" },
  pending_followup: { bg:"#fef9c3", text:"#854d0e", label:"Follow-up" },
};
function StatusChip({ status }) {
  const s = STATUS[status] || { bg:"#f3f4f6", text:"#374151", label: status };
  return (
    <span style={{ padding:"2px 9px", borderRadius:20, fontSize:11, fontWeight:700,
      background:s.bg, color:s.text, whiteSpace:"nowrap", display:"inline-block" }}>
      {s.label}
    </span>
  );
}

// ─── Follow-up Modal ──────────────────────────────────────────────────────────
function FollowUpModal({ trial, onClose, onSave }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const [notes, setNotes] = useState(trial.feedback_notes || "");
  const [date,  setDate]  = useState(trial.followup_date  || "");
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    await onSave(trial.id, { feedback_notes: notes, followup_date: date || null, status: "pending_followup" });
    setSaving(false);
    onClose();
  }

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:2000,
      display:"flex", alignItems:"center", justifyContent:"center" }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: isDark ? "#1e293b" : "#fff", borderRadius:16, padding:28,
        width:420, boxShadow:"0 24px 64px rgba(0,0,0,0.35)" }}>
        <div style={{ fontSize:15, fontWeight:700, color:T.textPrimary, marginBottom:18 }}>
          Follow-up — <span style={{ color:getBrandColors().blue }}>{trial.student_name}</span>
        </div>
        <label style={{ display:"block", fontSize:11, fontWeight:700, letterSpacing:"0.08em",
          textTransform:"uppercase", color:T.textLabel, marginBottom:5 }}>Notes</label>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
          style={{ width:"100%", borderRadius:8, border:`1.5px solid ${T.inputBdr}`,
            padding:"9px 12px", fontSize:13, color:T.inputText, background:T.inputBg,
            boxSizing:"border-box", resize:"vertical", marginBottom:14, outline:"none" }} />
        <label style={{ display:"block", fontSize:11, fontWeight:700, letterSpacing:"0.08em",
          textTransform:"uppercase", color:T.textLabel, marginBottom:5 }}>Next Follow-up Date</label>
        <input type="date" value={date} onChange={e => setDate(e.target.value)}
          style={{ width:"100%", borderRadius:8, border:`1.5px solid ${T.inputBdr}`,
            padding:"9px 12px", fontSize:13, color:T.inputText, background:T.inputBg,
            boxSizing:"border-box", marginBottom:22, outline:"none" }} />
        <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
          <button onClick={onClose} style={{ padding:"8px 18px", borderRadius:8,
            border:`1px solid ${T.inputBdr}`, background:"transparent",
            color:T.textSecondary, cursor:"pointer", fontSize:13 }}>Cancel</button>
          <button onClick={save} disabled={saving} style={{ padding:"8px 20px", borderRadius:8,
            border:"none", background:getBrandColors().blue, color:"#fff", cursor:"pointer",
            fontSize:13, fontWeight:700 }}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Decline Modal ────────────────────────────────────────────────────────────
function DeclineModal({ trial, onClose, onSave }) {
  const { isDark } = useTheme();
  const T = getT(isDark);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    await fetch(`${API_BASE}/trials/inquiries/${trial.id}/never-signup?reason=${encodeURIComponent(reason)}`,
      { method:"PUT" });
    onSave(trial.id, { status:"never_signup", internal_notes: reason ? `Reason: ${reason}` : null });
    setSaving(false);
    onClose();
  }

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:2000,
      display:"flex", alignItems:"center", justifyContent:"center" }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: isDark ? "#1e293b" : "#fff", borderRadius:16, padding:28,
        width:380, boxShadow:"0 24px 64px rgba(0,0,0,0.35)" }}>
        <div style={{ fontSize:15, fontWeight:700, color:T.textPrimary, marginBottom:14 }}>
          Decline — <span style={{ color:getBrandColors().red }}>{trial.student_name}</span>
        </div>
        <label style={{ display:"block", fontSize:11, fontWeight:700, letterSpacing:"0.08em",
          textTransform:"uppercase", color:T.textLabel, marginBottom:5 }}>Reason (optional)</label>
        <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
          style={{ width:"100%", borderRadius:8, border:`1.5px solid ${T.inputBdr}`,
            padding:"9px 12px", fontSize:13, color:T.inputText, background:T.inputBg,
            boxSizing:"border-box", resize:"vertical", marginBottom:22, outline:"none" }} />
        <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
          <button onClick={onClose} style={{ padding:"8px 18px", borderRadius:8,
            border:`1px solid ${T.inputBdr}`, background:"transparent",
            color:T.textSecondary, cursor:"pointer", fontSize:13 }}>Cancel</button>
          <button onClick={save} disabled={saving} style={{ padding:"8px 20px", borderRadius:8,
            border:"none", background:getBrandColors().red, color:"#fff", cursor:"pointer",
            fontSize:13, fontWeight:700 }}>
            {saving ? "Saving…" : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Action Icons ─────────────────────────────────────────────────────────────
function ActionIcons({ trial, onPatch, isDark }) {
  const [followUp, setFollowUp] = useState(false);
  const [decline,  setDecline]  = useState(false);
  const [busy,     setBusy]     = useState(null); // "checkin"|"noshow"

  const status = trial.status;
  const canCheckIn  = status === "booked";
  const canNoShow   = status === "booked";
  const canFollowUp = ["booked","attended","pending_followup"].includes(status);
  const canDecline  = !["never_signup","converted"].includes(status);
  // Enrol is available in every live state (booked, checked-in/attended, no-show,
  // pending follow-up). It is blocked only once the inquiry was declined
  // (never_signup) or has already been converted to Student Master.
  const canConvert  = !["never_signup","converted"].includes(status);

  async function convert() {
    if (!window.confirm(`Mark ${trial.student_name} as Converted (enrolled to Student Master)? This updates the conversion funnel only — no student record is created. You can undo it.`)) return;
    setBusy("convert");
    onPatch(trial.id, { prev_status: trial.status, status:"converted" }); // optimistic
    try { await api.put(`/trials/inquiries/${trial.id}/convert`); }
    catch(e) { onPatch(trial.id, { status: trial.status }); }
    setBusy(null);
  }

  async function undoConvert() {
    const restore = trial.prev_status || (trial.checked_in ? "attended" : "booked");
    setBusy("undo");
    onPatch(trial.id, { status: restore, prev_status: null }); // optimistic
    try { await api.put(`/trials/inquiries/${trial.id}/convert/undo`); }
    catch(e) { onPatch(trial.id, { status: "converted" }); }
    setBusy(null);
  }

  async function checkIn() {
    setBusy("checkin");
    onPatch(trial.id, { status:"attended", checked_in:1 }); // optimistic
    try { await api.put(`/trials/inquiries/${trial.id}/checkin`); }
    catch(e) { onPatch(trial.id, { status: trial.status, checked_in: trial.checked_in }); }
    setBusy(null);
  }

  async function noShow() {
    setBusy("noshow");
    onPatch(trial.id, { status:"no_show" }); // optimistic
    try { await api.post(`/trials/inquiries/${trial.id}/no-show`); }
    catch(e) { onPatch(trial.id, { status: trial.status }); }
    setBusy(null);
  }

  function saveFollowUp(id, body) {
    onPatch(id, { status:"pending_followup", feedback_notes: body.feedback_notes, followup_date: body.followup_date });
    return api.put(`/trials/inquiries/${id}/notes`, body);
  }

  const iconBtn = (title, color, disabled, onClick, children) => (
    <button title={title} onClick={onClick} disabled={disabled}
      style={{ background:"none", border:"none", cursor: disabled ? "default" : "pointer",
        color: disabled ? (isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)") : color,
        fontSize:16, padding:"2px 5px", borderRadius:6, transition:"opacity 0.15s",
        opacity: disabled ? 0.4 : 1, display:"inline-flex", alignItems:"center" }}>
      {children}
    </button>
  );

  return (
    <div style={{ display:"flex", alignItems:"center", gap:2, justifyContent:"flex-end" }}>
      {iconBtn("Check In", "#16a34a", !canCheckIn || busy === "checkin", checkIn,
        busy === "checkin" ? <span style={{fontSize:11}}>…</span> : <FaCheckCircle />)}
      {status === "converted"
        ? iconBtn("Undo conversion", getBrandColors().orange, busy === "undo", undoConvert,
            busy === "undo" ? <span style={{fontSize:11}}>…</span> : <FaUndo />)
        : iconBtn("Enrol to Student Master", "#7c3aed", !canConvert || busy === "convert", convert,
            busy === "convert" ? <span style={{fontSize:11}}>…</span> : <FaUserGraduate />)}
      {iconBtn("Follow-up", "#2563eb", !canFollowUp, () => setFollowUp(true), <FaCalendarAlt />)}
      {iconBtn("No Show",   getBrandColors().red, !canNoShow || busy === "noshow",  noShow,
        busy === "noshow"  ? <span style={{fontSize:11}}>…</span> : <FaTimesCircle />)}
      {iconBtn("Decline",   "#9ca3af", !canDecline, () => setDecline(true), <FaBan />)}

      {followUp && <FollowUpModal trial={trial} onClose={() => setFollowUp(false)} onSave={saveFollowUp} />}
      {decline  && <DeclineModal  trial={trial} onClose={() => setDecline(false)}
        onSave={(id, patch) => onPatch(id, patch)} />}
    </div>
  );
}

// ─── Trials List (shared by This Week + All Trials) ───────────────────────────
const SORT_KEYS = {
  "Student":         (t) => (t.student_name || "").toLowerCase(),
  "Parent / Contact":(t) => (t.parent_name  || "").toLowerCase(),
  "Date & Time":     (t) => t.free_trial_date || "",
  "Status":          (t) => t.status || "",
  "Follow-up":       (t) => t.followup_date  || "",
};

function TrialsList({ trials, onPatch, isDark, emptyMsg }) {
  const T = getT(isDark);
  const [expanded, setExpanded] = useState(null);
  const [sortCol,  setSortCol]  = useState("Date & Time");
  const [sortAsc,  setSortAsc]  = useState(true);
  const { fmtDate } = useDateTimeFormat();

  const rowBg    = isDark ? "rgba(255,255,255,0.04)" : "#fff";
  const rowHover = isDark ? "rgba(255,255,255,0.07)" : "#f8fafc";
  const bdr      = isDark ? "rgba(255,255,255,0.07)" : "rgba(3,78,123,0.08)";

  function toggleSort(col) {
    if (!SORT_KEYS[col]) return;
    if (sortCol === col) setSortAsc(v => !v);
    else { setSortCol(col); setSortAsc(true); }
  }

  const sorted = [...trials].sort((a, b) => {
    const fn = SORT_KEYS[sortCol];
    if (!fn) return 0;
    const av = fn(a), bv = fn(b);
    return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  if (!trials.length) return (
    <div style={{ textAlign:"center", padding:"36px 0", color:T.textSecondary, fontSize:13 }}>
      {emptyMsg || "No trials found."}
    </div>
  );

  const COLS = ["Student","Parent / Contact","Date & Time","Status","Follow-up","Actions"];

  return (
    <div style={{ borderRadius:10, overflow:"hidden", border:`1px solid ${bdr}` }}>
      {/* Header */}
      <div style={{ display:"grid", gridTemplateColumns:"2fr 1.4fr 1fr 80px 110px 110px",
        gap:0, background: isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9",
        padding:"7px 14px", borderBottom:`1px solid ${bdr}` }}>
        {COLS.map(h => {
          const sortable = !!SORT_KEYS[h];
          const active   = sortCol === h;
          return (
            <div key={h}
              onClick={() => toggleSort(h)}
              style={{ fontSize:11, fontWeight:700, letterSpacing:"0.07em",
                textTransform:"uppercase", color: active ? (isDark ? "#93c5fd" : getBrandColors().blue) : T.textLabel,
                cursor: sortable ? "pointer" : "default",
                display:"flex", alignItems:"center", gap:4, userSelect:"none" }}>
              {h}
              {sortable && (
                <span style={{ fontSize:9, opacity: active ? 1 : 0.35 }}>
                  {active ? (sortAsc ? "▲" : "▼") : "⇅"}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {sorted.map((t, idx) => {
        const isExp = expanded === t.id;
        const hasMeta = t.comments || t.feedback_notes;
        return (
          <React.Fragment key={t.id}>
            <div style={{
              display:"grid", gridTemplateColumns:"2fr 1.4fr 1fr 80px 110px 110px",
              gap:0, padding:"9px 14px", alignItems:"center",
              borderBottom: idx < trials.length - 1 ? `1px solid ${bdr}` : "none",
              background: isExp ? rowHover : rowBg,
              transition:"background 0.12s",
            }}>
              {/* Student */}
              <div>
                <div style={{ fontSize:13, fontWeight:600, color:T.textPrimary,
                  display:"flex", alignItems:"center", gap:5 }}>
                  {t.student_name}
                  {hasMeta && (
                    <button onClick={() => setExpanded(isExp ? null : t.id)}
                      style={{ background:"none", border:"none", cursor:"pointer",
                        color:T.textSecondary, padding:0, fontSize:11, display:"flex" }}>
                      {isExp ? <FaChevronUp /> : <FaChevronDown />}
                    </button>
                  )}
                </div>
                <div style={{ fontSize:11, color:T.textSecondary, marginTop:1 }}>{t.email}</div>
              </div>

              {/* Parent */}
              <div>
                <div style={{ fontSize:12, color:T.textSecondary }}>{t.parent_name}</div>
                <div style={{ fontSize:11, color:T.textSecondary, marginTop:1 }}>{t.phone}</div>
              </div>

              {/* Date & Time */}
              <div>
                <div style={{ fontSize:12, color:T.textPrimary, fontWeight:500 }}>
                  {fmtDate(t.free_trial_date)}
                </div>
                <div style={{ fontSize:11, color:T.textSecondary, marginTop:1 }}>
                  {t.free_trial_time || "18:30"}
                </div>
              </div>

              {/* Status */}
              <div><StatusChip status={t.status} /></div>

              {/* Follow-up date */}
              <div style={{ fontSize:11, color: t.followup_date ? "#2563eb" : T.textSecondary }}>
                {t.followup_date ? fmtDate(t.followup_date) : "—"}
              </div>

              {/* Actions */}
              <ActionIcons trial={t} onPatch={onPatch} isDark={isDark} />
            </div>

            {/* Expanded notes row */}
            {isExp && (
              <div style={{ padding:"8px 14px 10px 28px",
                background: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
                borderBottom: idx < trials.length - 1 ? `1px solid ${bdr}` : "none",
                fontSize:12, color:T.textSecondary, lineHeight:1.7 }}>
                {t.comments && <div><strong>Comments:</strong> {t.comments}</div>}
                {t.feedback_notes && <div><strong>Notes:</strong> {t.feedback_notes}</div>}
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Conversion Funnel ────────────────────────────────────────────────────────
const FUNNEL_STATUS = {
  booked:           { label:"Booked",    color:"#3b82f6" },
  attended:         { label:"Attended",  color:"#22c55e" },
  pending_followup: { label:"Follow-up", color:"#f59e0b" },
  converted:        { label:"Converted", color:getBrandColors().green },
  no_show:          { label:"No Show",   color:"#ef4444" },
  never_signup:     { label:"Declined",  color:"#94a3b8" },
};
const FUNNEL_ORDER = ["booked","attended","pending_followup","converted","no_show","never_signup"];

function ConversionFunnel({ stats, isDark }) {
  const T = getT(isDark);
  const val = k => stats[`total_${k}`] || 0;
  const total = stats.total_all || FUNNEL_ORDER.reduce((s,k)=>s+val(k),0) || 0;
  const denom = total || 1;
  const showedUp  = val("attended") + val("pending_followup") + val("converted");
  const converted = val("converted");
  const rate = total ? Math.round((converted/denom)*100) : 0;
  const pct  = v => Math.round((v/denom)*100);

  const cardBg  = isDark ? "rgba(255,255,255,0.05)" : "#f8fafc";
  const cardBdr = isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #e2e8f0";
  const track   = isDark ? "rgba(255,255,255,0.08)" : "#e5e9f0";

  const hero = [
    { label:"Total Inquiries", value: total,        color: T.textPrimary },
    { label:"Converted",       value: converted,    color:getBrandColors().green },
    { label:"Conversion Rate", value: rate + "%",   color:getBrandColors().blue },
  ];
  // SVG funnel geometry (centred trapezoids that taper by value).
  // Min half-width keeps every band wide enough for its label to stay inside.
  const cx = 150, HW = 140, MINHW = 52;
  const hw = v => Math.max((v / denom) * HW, MINHW);
  const h0 = HW, h1 = hw(showedUp), h2 = hw(converted), h2b = Math.max(h2 * 0.78, 40);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:22 }}>
      {/* Hero metrics */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:12 }}>
        {hero.map(h => (
          <div key={h.label} style={{ background:cardBg, border:cardBdr, borderRadius:12, padding:"16px 18px" }}>
            <div style={{ fontSize:10.5, fontWeight:700, letterSpacing:"0.06em", textTransform:"uppercase", color:T.textSecondary }}>{h.label}</div>
            <div style={{ fontSize:30, fontWeight:800, color:h.color, marginTop:4 }}>{h.value}</div>
          </div>
        ))}
      </div>

      {/* Funnel (SVG) + status breakdown */}
      <div style={{ display:"grid", gridTemplateColumns:"minmax(0,290px) 1fr", gap:24, alignItems:"center" }}>
        <div>
          <div style={{ fontSize:13, fontWeight:700, color:T.textPrimary, marginBottom:8 }}>Conversion pipeline</div>
          <svg viewBox="0 0 300 250" width="100%" role="img" aria-label="Conversion funnel">
            <defs>
              <linearGradient id="ftg0" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#60a5fa"/><stop offset="1" stopColor="#2563eb"/></linearGradient>
              <linearGradient id="ftg1" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#4ade80"/><stop offset="1" stopColor="#16a34a"/></linearGradient>
              <linearGradient id="ftg2" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#10b981"/><stop offset="1" stopColor="#047857"/></linearGradient>
            </defs>
            <polygon points={`${cx-h0},8 ${cx+h0},8 ${cx+h1},80 ${cx-h1},80`} fill="url(#ftg0)"/>
            <text x={cx} y="33" textAnchor="middle" fill="#fff" fontSize="11" fontWeight="600" opacity="0.92">Total inquiries</text>
            <text x={cx} y="60" textAnchor="middle" fill="#fff" fontSize="19" fontWeight="800">{total} · 100%</text>
            <polygon points={`${cx-h1},86 ${cx+h1},86 ${cx+h2},158 ${cx-h2},158`} fill="url(#ftg1)"/>
            <text x={cx} y="111" textAnchor="middle" fill="#fff" fontSize="11" fontWeight="600" opacity="0.92">Showed up</text>
            <text x={cx} y="138" textAnchor="middle" fill="#fff" fontSize="17" fontWeight="800">{showedUp} · {pct(showedUp)}%</text>
            <polygon points={`${cx-h2},164 ${cx+h2},164 ${cx+h2b},236 ${cx-h2b},236`} fill="url(#ftg2)"/>
            <text x={cx} y="189" textAnchor="middle" fill="#fff" fontSize="10.5" fontWeight="600" opacity="0.92">Converted</text>
            <text x={cx} y="215" textAnchor="middle" fill="#fff" fontSize="15" fontWeight="800">{converted} · {pct(converted)}%</text>
          </svg>
        </div>

        <div>
          <div style={{ fontSize:13, fontWeight:700, color:T.textPrimary, marginBottom:10 }}>By status — all inquiries</div>
          <div style={{ display:"flex", height:24, borderRadius:7, overflow:"hidden", background:track, marginBottom:12 }}>
            {FUNNEL_ORDER.map(k => {
              const v = val(k); if (!v) return null;
              return <div key={k} title={`${FUNNEL_STATUS[k].label}: ${v} (${pct(v)}%)`}
                style={{ width:`${(v/denom)*100}%`, background:FUNNEL_STATUS[k].color }} />;
            })}
          </div>
          <div style={{ display:"flex", flexDirection:"column" }}>
            {FUNNEL_ORDER.map(k => {
              const v = val(k);
              return (
                <div key={k} style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 2px", borderBottom:cardBdr }}>
                  <span style={{ width:11, height:11, borderRadius:3, background:FUNNEL_STATUS[k].color, flexShrink:0 }} />
                  <span style={{ flex:1, fontSize:12.5, fontWeight:600, color:T.textSecondary }}>{FUNNEL_STATUS[k].label}</span>
                  <span style={{ fontSize:15, fontWeight:800, color:T.textPrimary }}>{v}</span>
                  <span style={{ width:36, textAlign:"right", fontSize:11.5, color:T.textSecondary, fontWeight:600 }}>{pct(v)}%</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Time snapshot */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:12 }}>
        {[
          { label:"Today",      value:stats.today_count },
          { label:"This Week",  value:stats.this_week_count },
          { label:"This Month", value:stats.this_month_count },
        ].map(s => (
          <div key={s.label} style={{ textAlign:"center", background:cardBg, border:cardBdr, borderRadius:10, padding:"10px 8px" }}>
            <div style={{ fontSize:20, fontWeight:800, color:T.textPrimary }}>{s.value}</div>
            <div style={{ fontSize:10, color:T.textSecondary, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.06em" }}>{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function FreeTrials() {
  const { isDark } = useTheme();
  const T = getT(isDark);

  const [tab,     setTab]    = useState("week");
  const [trials,  setTrials] = useState([]);
  const [stats,   setStats]  = useState(null);
  const [loading, setLoading] = useState(true);

  // Filter state (shared, preserved across tab switches)
  const [search,     setSearch]     = useState("");
  const [statusFilt, setStatusFilt] = useState("all");
  const [dateRange,  setDateRange]  = useState("week");   // "today"|"week"|"month"|"all"|"custom"
  const [customFrom, setCustomFrom] = useState("");
  const [customTo,   setCustomTo]   = useState("");

  const bgRef = useRef(null); // background refresh timer

  // ── Load all trials + stats once ────────────────────────────────────────────
  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [allData, statsData] = await Promise.all([
        api.get("/trials/inquiries"),
        api.get("/trials/stats"),
      ]);
      setTrials(Array.isArray(allData) ? allData : []);
      setStats(statsData);
    } catch (e) {
      console.error("FreeTrials load error:", e);
    }
    if (!silent) setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Optimistic patch — updates state immediately, syncs quietly in bg ───────
  function patchTrial(id, patch) {
    setTrials(prev => prev.map(t => t.id === id ? { ...t, ...patch } : t));
    // Silently refresh stats after a short delay
    clearTimeout(bgRef.current);
    bgRef.current = setTimeout(() => load(true), 1500);
  }

  // ── Filter logic ────────────────────────────────────────────────────────────
  const visible = trials.filter(t => {
    // Date range filter
    const d = t.free_trial_date ? t.free_trial_date.slice(0, 10) : "";
    if (dateRange === "today"  && d !== TODAY) return false;
    if (dateRange === "week"   && (d < WK.start || d > WK.end)) return false;
    if (dateRange === "month"  && (d < MO.start || d > MO.end)) return false;
    if (dateRange === "custom") {
      if (customFrom && d < customFrom) return false;
      if (customTo   && d > customTo)   return false;
    }
    // Status filter
    if (statusFilt !== "all" && t.status !== statusFilt) return false;
    // Text search
    const q = search.toLowerCase();
    if (q && !t.student_name?.toLowerCase().includes(q) &&
             !t.parent_name?.toLowerCase().includes(q) &&
             !t.email?.toLowerCase().includes(q) &&
             !t.phone?.includes(q)) return false;
    return true;
  });

  // Week view: same filter but forced to "week"
  const weekTrials = trials.filter(t => {
    const d = t.free_trial_date ? t.free_trial_date.slice(0, 10) : "";
    return d >= WK.start && d <= WK.end;
  });

  // ── UI helpers ───────────────────────────────────────────────────────────────
  const panelBg  = isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.85)";
  const panelBdr = isDark ? "1.5px solid rgba(255,255,255,0.10)" : "1.5px solid rgba(3,78,123,0.10)";

  const tabBtn = (key, label) => (
    <button key={key} onClick={() => setTab(key)}
      style={{ padding:"8px 18px", borderRadius:9, border:"none", cursor:"pointer",
        fontSize:13, fontWeight:700, whiteSpace:"nowrap",
        background: tab === key ? getBrandColors().blue : (isDark ? "rgba(255,255,255,0.08)" : "rgba(3,78,123,0.07)"),
        color: tab === key ? "#fff" : T.textSecondary }}>
      {label}
    </button>
  );

  const quickBtn = (val, label) => (
    <button key={val} onClick={() => setDateRange(val)}
      style={{ padding:"5px 13px", borderRadius:7, border:"none", cursor:"pointer",
        fontSize:12, fontWeight:600,
        background: dateRange === val
          ? (isDark ? "rgba(3,78,123,0.70)" : getBrandColors().blue)
          : (isDark ? "rgba(255,255,255,0.08)" : "rgba(3,78,123,0.07)"),
        color: dateRange === val ? "#fff" : T.textSecondary }}>
      {label}
    </button>
  );

  const weekLabel = (() => {
    const opts = { month:"short", day:"numeric" };
    const s = new Date(WK.start + "T00:00:00");
    const e = new Date(WK.end   + "T00:00:00");
    return `${s.toLocaleDateString("en-US", opts)} – ${e.toLocaleDateString("en-US", opts)}`;
  })();

  return (
    <div style={{ padding:"24px 28px", fontFamily:"'Segoe UI', system-ui, sans-serif", maxWidth:1160 }}>

      {/* Header */}
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between",
        marginBottom:20, flexWrap:"wrap", gap:10 }}>
        <div style={{ display:"flex", alignItems:"center", gap:14 }}>
          <PageHeaderIcon icon={<FaCalendarAlt size={20} color={getBrandColors().blueDark} />} />
          <div>
            <h1 style={{ margin:0, fontSize:26, fontWeight:800, color:isDark?"#93c5fd":getBrandColors().blue }}>
              Free Trials
            </h1>
            <p style={{ margin:"4px 0 0", fontSize:13, color:T.textSecondary }}>
              Track sign-ups, check-ins and follow-ups from website bookings.
            </p>
          </div>
        </div>
        <button onClick={() => load(false)}
          style={{ padding:"7px 16px", borderRadius:9, border:`1px solid ${T.inputBdr}`,
            background:"transparent", color:T.textSecondary, cursor:"pointer", fontSize:13 }}>
          ↻ Refresh
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display:"flex", gap:4, marginBottom:18, flexWrap:"wrap" }}>
        {tabBtn("week",   `📅 This Week (${weekTrials.length})`)}
        {tabBtn("all",    "All Trials")}
        {tabBtn("funnel", "Conversion Funnel")}
      </div>

      {/* Panel */}
      <div style={{ background:panelBg, border:panelBdr, borderRadius:16,
        padding:"20px 22px", backdropFilter:"blur(18px)", WebkitBackdropFilter:"blur(18px)" }}>

        {loading && (
          <div style={{ textAlign:"center", padding:40, color:T.textSecondary, fontSize:13 }}>
            Loading…
          </div>
        )}

        {/* ── This Week ── */}
        {!loading && tab === "week" && (
          <div>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
              marginBottom:14, flexWrap:"wrap", gap:8 }}>
              <div>
                <span style={{ fontSize:14, fontWeight:700, color:T.textPrimary }}>
                  This Week
                </span>
                <span style={{ fontSize:12, color:T.textSecondary, marginLeft:10 }}>
                  {weekLabel}
                </span>
              </div>
              {/* Quick status filter for week view */}
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {["all","booked","attended","pending_followup","converted","no_show"].map(s => (
                  <button key={s} onClick={() => setStatusFilt(s)}
                    style={{ padding:"4px 11px", borderRadius:7, border:"none",
                      cursor:"pointer", fontSize:11, fontWeight:600,
                      background: statusFilt === s
                        ? (STATUS[s]?.bg || getBrandColors().blue)
                        : (isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"),
                      color: statusFilt === s
                        ? (STATUS[s]?.text || "#fff")
                        : T.textSecondary }}>
                    {s === "all" ? "All" : (STATUS[s]?.label || s)}
                  </button>
                ))}
              </div>
            </div>

            <TrialsList
              trials={weekTrials.filter(t =>
                (statusFilt === "all" || t.status === statusFilt)
              )}
              onPatch={patchTrial}
              isDark={isDark}
              emptyMsg="No free trials scheduled this week."
            />
          </div>
        )}

        {/* ── All Trials ── */}
        {!loading && tab === "all" && (
          <div>
            {/* Filter bar */}
            <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap",
              alignItems:"center" }}>
              {/* Search */}
              <input
                placeholder="Search name, email, phone…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ flex:"1 1 160px", minWidth:140, padding:"7px 13px", borderRadius:8,
                  border:`1.5px solid ${T.inputBdr}`, background:T.inputBg,
                  color:T.inputText, fontSize:13, outline:"none" }}
              />

              {/* Status */}
              <select value={statusFilt} onChange={e => setStatusFilt(e.target.value)}
                style={{ padding:"7px 12px", borderRadius:8, border:`1.5px solid ${T.inputBdr}`,
                  background:T.inputBg, color:T.inputText, fontSize:13, outline:"none" }}>
                <option value="all">All Statuses</option>
                {Object.entries(STATUS).map(([k,v]) =>
                  <option key={k} value={k}>{v.label}</option>)}
              </select>

              {/* Quick date selectors */}
              <div style={{ display:"flex", gap:4 }}>
                {quickBtn("today", "Today")}
                {quickBtn("week",  "This Week")}
                {quickBtn("month", "This Month")}
                {quickBtn("all",   "All Time")}
                {quickBtn("custom","Custom")}
              </div>
            </div>

            {/* Custom date range inputs */}
            {dateRange === "custom" && (
              <div style={{ display:"flex", gap:8, marginBottom:14, alignItems:"center" }}>
                <span style={{ fontSize:12, color:T.textSecondary }}>From</span>
                <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                  style={{ padding:"6px 10px", borderRadius:7, border:`1.5px solid ${T.inputBdr}`,
                    background:T.inputBg, color:T.inputText, fontSize:12, outline:"none" }} />
                <span style={{ fontSize:12, color:T.textSecondary }}>To</span>
                <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                  style={{ padding:"6px 10px", borderRadius:7, border:`1.5px solid ${T.inputBdr}`,
                    background:T.inputBg, color:T.inputText, fontSize:12, outline:"none" }} />
                <span style={{ fontSize:12, color:T.textSecondary }}>
                  {visible.length} result{visible.length !== 1 ? "s" : ""}
                </span>
              </div>
            )}

            <TrialsList
              trials={visible}
              onPatch={patchTrial}
              isDark={isDark}
              emptyMsg="No trials match the current filters."
            />
          </div>
        )}

        {/* ── Funnel ── */}
        {!loading && tab === "funnel" && stats && (
          <ConversionFunnel stats={stats} isDark={isDark} />
        )}
      </div>

      {/* Icon legend */}
      <div style={{ display:"flex", gap:18, marginTop:12, flexWrap:"wrap" }}>
        {[
          { icon:<FaCheckCircle color="#16a34a"/>, label:"Check In" },
          { icon:<FaCalendarAlt color="#2563eb"/>, label:"Follow-up" },
          { icon:<FaTimesCircle color={getBrandColors().red}/>, label:"No Show" },
          { icon:<FaBan         color="#9ca3af"/>, label:"Decline" },
        ].map(({ icon, label }) => (
          <div key={label} style={{ display:"flex", alignItems:"center", gap:5,
            fontSize:11, color:T.textSecondary }}>
            {icon} {label}
          </div>
        ))}
        <span style={{ fontSize:11, color:T.textSecondary, marginLeft:"auto", fontStyle:"italic" }}>
          Actions update instantly — synced silently in background
        </span>
      </div>
    </div>
  );
}
